import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Thread } from "./Thread";
import { IntakeForm } from "./IntakeForm";
import { ANDREA_PHONE, ANDREA_PHONE_DISPLAY, contactClick } from "../lib/contact";
import { trackEvent } from "../lib/tracking";
import { STAGE_CONTENT, SERVICE_CONTENT, JOURNEY_STEPS } from "../pages/clientJourney";

// The six private post-payment welcome pages — /virtual-newborn …
// /in-home-toddler — rendered by this one template. Parents arrive via
// /payment-complete (which routes on the webhook-verified purchase) or their
// emailed link. Verification: ?session_id= is checked server-side against
// the paid purchase; the browser stamp is the same-device fallback. A
// mismatched route self-heals by redirecting to the client's actual route.
// Fires no purchase/conversion events — Stripe's webhook stays the source of
// truth. Intake completion is the parent's own "I've Submitted My Intake"
// tap (Google Forms cannot notify us), which marks the client record, sends
// the scheduling email, and forwards to the Sleep Plan Review page.
const API = process.env.REACT_APP_BACKEND_URL;
const STAMP_KEY = "tnpc_pending_payment";
const STAMP_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const PostPayment = ({ service, stage }) => {
  const reduce = useReducedMotion();
  const [state, setState] = useState(null); // null = resolving | "none" | data
  const [submitting, setSubmitting] = useState(false);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    document.title = "You're in — The Nurtured Parent Co.";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    window.scrollTo(0, 0);

    let cancelled = false;
    (async () => {
      try {
        let body = null;
        if (sessionId) {
          body = { session_id: sessionId };
        } else {
          const raw = localStorage.getItem(STAMP_KEY);
          if (raw) {
            const stamp = JSON.parse(raw);
            const fresh = stamp?.ts && Date.now() - stamp.ts < STAMP_TTL_MS;
            const staged = ["newborn", "infant", "toddler"].includes(stamp?.stage);
            if (fresh && staged) {
              body = { slug: stamp.slug, service_type: stamp.service, client_stage: stamp.stage };
            }
          }
        }
        if (!body) {
          if (!cancelled) setState("none");
          return;
        }
        const resp = await fetch(`${API}/api/pay/intake`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!resp.ok) throw new Error(`lookup failed: ${resp.status}`);
        const data = await resp.json();
        // Self-heal a wrong-route landing: send the parent to THEIR route.
        if (sessionId && data.service_type && data.client_stage &&
            (data.service_type !== service || data.client_stage !== stage)) {
          window.location.replace(`/${data.service_type}-${data.client_stage}?session_id=${sessionId}`);
          return;
        }
        if (!cancelled) setState(data);
      } catch {
        if (!cancelled) setState("none");
      }
    })();

    return () => {
      cancelled = true;
      document.head.removeChild(meta);
      document.title = "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });

  const stageInfo = STAGE_CONTENT[stage];
  const serviceInfo = SERVICE_CONTENT[service];
  const intakeDone = state && state !== "none" && state.intake_status === "complete";

  const openIntake = () => {
    trackEvent("intake_started", { service_type: service, client_stage: stage, intake_type: "google_form" });
    if (sessionId) {
      fetch(`${API}/api/journey/intake-started`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      }).catch(() => {});
    }
  };

  const submitIntakeDone = async () => {
    if (submitting) return;
    setSubmitting(true);
    trackEvent("intake_completed", { service_type: service, client_stage: stage, intake_type: "google_form" });
    if (!sessionId) {
      // Stamp-path fallback: nothing server-side to mark — the scheduling
      // page still opens, and Andrea's hub reflects status manually.
      window.location.assign("/schedule-sleep-plan-review");
      return;
    }
    try {
      const resp = await fetch(`${API}/api/journey/intake-complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await resp.json();
      window.location.assign(data.schedule_url || "/schedule-sleep-plan-review");
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="post-payment-page" className="min-h-svh bg-ivory text-ink flex flex-col">
      <header className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 py-8">
        <a href="/" data-testid="welcome-logo" className="inline-block">
          <span className="block font-serif font-light text-xl sm:text-2xl leading-tight tracking-tight">
            The Nurtured Parent Co.
          </span>
          <span className="block mt-1 text-[9px] tracking-[0.3em] uppercase text-smoke">
            Pediatric Sleep Consulting
          </span>
        </a>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20">
        {/* Post-payment hero */}
        <section className="py-14 lg:py-20">
          <motion.p {...rise(0.1)} className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
            Welcome to The Nurtured Parent Co.
          </motion.p>
          <motion.h1 {...rise(0.22)} className="mt-6 font-serif font-light text-ink text-5xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight">
            You&rsquo;re in the
            <span className="block italic text-clay">right place.</span>
          </motion.h1>
          <motion.p {...rise(0.36)} className="mt-8 font-serif italic font-light text-clay text-2xl sm:text-3xl leading-snug max-w-2xl">
            You don&rsquo;t have to figure this out alone.
          </motion.p>
          <motion.div {...rise(0.5)} className="mt-10 max-w-lg space-y-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
            <p>
              Your payment is received, and Andrea now has you on her client
              list. Your next step is your intake — where you tell her what
              sleep actually looks like in your house right now.
            </p>
            <p>
              Andrea personally reviews everything you share. It&rsquo;s how
              your recommendations get built around your actual family — not a
              generic schedule.
            </p>
          </motion.div>

          {/* Payment confirmation — a quiet card, never an invoice */}
          <motion.div {...rise(0.62)} data-testid="payment-confirmed" className="mt-12 max-w-lg rounded-3xl border border-ink/10 bg-linen/60 p-6">
            <p className="flex items-center gap-2.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-euca">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-euca/15 text-euca">
                <Check size={12} strokeWidth={3} />
              </span>
              Payment confirmed
            </p>
            <p data-testid="welcome-program" className="mt-3 font-serif font-light text-ink text-2xl tracking-tight">
              {serviceInfo.label}
            </p>
            <p data-testid="welcome-stage" className="mt-1 text-sm text-smoke">
              {stageInfo.label} · {stageInfo.ages}
            </p>
          </motion.div>
        </section>

        {/* What happens next */}
        <section className="py-14 lg:py-20 border-t border-ink/10">
          <motion.div {...rise(0.1)} className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              What happens next
            </p>
            <div className="mt-8">
              {JOURNEY_STEPS.map((s, i) => (
                <div key={s.n} data-testid={`journey-step-${i}`} className="py-6 border-t border-ink/10 last:border-b flex items-baseline gap-6">
                  <span aria-hidden="true" className="font-serif italic font-light text-clay text-xl shrink-0">{s.n}</span>
                  <div>
                    <p className="font-serif font-light text-ink text-xl sm:text-2xl leading-snug">{s.title}</p>
                    <p className="mt-1.5 text-sm sm:text-base font-normal leading-relaxed text-smoke">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
              {stageInfo.acknowledge}
            </p>
            <p className="mt-4 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
              {serviceInfo.line}
            </p>
          </motion.div>
        </section>

        {/* The intake — primary action + completion confirmation */}
        <section className="py-14 lg:py-20 border-t border-ink/10">
          <motion.div {...rise(0.1)} className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Your next step
            </p>

            {state === null && (
              <p data-testid="welcome-loading" className="mt-8 text-base font-normal text-smoke">
                One moment — preparing your intake…
              </p>
            )}

            {state === "none" && (
              <div data-testid="welcome-private" className="mt-8 max-w-xl space-y-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
                <p>
                  This is a private client page — the link to yours arrived in
                  your confirmation email right after payment.
                </p>
                <p>If you can&rsquo;t find it, text Andrea and she&rsquo;ll send it straight over.</p>
                <a
                  data-testid="welcome-text-andrea"
                  href={`sms:${ANDREA_PHONE}`}
                  onClick={(e) => {
                    trackEvent("text_andrea_click", { location: "post_payment" });
                    contactClick("text")(e);
                  }}
                  className="inline-flex items-center gap-2.5 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300"
                >
                  Text Andrea — {ANDREA_PHONE_DISPLAY}
                </a>
              </div>
            )}

            {state && state !== "none" && !intakeDone && sessionId && (
              <>
                <h2 className="mt-6 font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.12] tracking-tight">
                  {stageInfo.intakeLabel}
                </h2>
                <div className="mt-8">
                  <IntakeForm
                    stage={stage}
                    serviceType={service}
                    sessionId={sessionId}
                    draft={state.intake_draft || {}}
                    draftOthers={state.intake_draft_others || {}}
                    parentName={state.parent_name}
                    onSubmitted={(url) => window.location.assign(url)}
                  />
                </div>
              </>
            )}

            {state && state !== "none" && !intakeDone && !sessionId && (
              <>
                <h2 className="mt-6 font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.12] tracking-tight">
                  {stageInfo.intakeLabel}
                </h2>
                <p className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
                  Take your time — there are no perfect answers. Andrea needs
                  the real picture, not the polished one.
                </p>
                <a
                  data-testid="intake-cta"
                  href={state.intake_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openIntake}
                  className="mt-9 w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-10 py-5 text-lg hover:bg-espresso focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-ivory transition-colors duration-300 group"
                >
                  Complete Your Intake
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <div className="mt-10 max-w-xl rounded-3xl border border-ink/10 bg-linen/60 p-6">
                  <p className="text-sm font-medium text-ink">
                    Finished and submitted the form?
                  </p>
                  <p className="mt-2 text-sm font-normal leading-relaxed text-smoke">
                    Tap below only after you&rsquo;ve submitted it — that&rsquo;s
                    what lets Andrea know your intake is ready to review, and
                    brings you to scheduling your Sleep Plan Review.
                  </p>
                  <button
                    data-testid="intake-done-btn"
                    onClick={submitIntakeDone}
                    disabled={submitting}
                    className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full border border-ink/25 text-ink font-medium px-8 py-4 text-base hover:border-ink disabled:opacity-60 transition-colors duration-300"
                  >
                    {submitting ? "One moment…" : "I've Submitted My Intake"}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </>
            )}

            {intakeDone && (
              <div data-testid="intake-complete-note" className="mt-8 max-w-xl">
                <p className="flex items-center gap-2.5 text-sm font-medium text-ink">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-euca/15 text-euca">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  Your intake is complete — Andrea has everything she needs to begin.
                </p>
                <a
                  data-testid="continue-scheduling-link"
                  href={sessionId ? `/schedule-sleep-plan-review?session_id=${sessionId}` : "/schedule-sleep-plan-review"}
                  className="mt-7 w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-10 py-5 text-lg hover:bg-espresso transition-colors duration-300 group"
                >
                  Continue to Scheduling
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            )}
          </motion.div>
        </section>

        {/* Andrea reassurance */}
        <section className="py-14 lg:py-20 border-t border-ink/10">
          <motion.div {...rise(0.1)} data-testid="andrea-review-block" className="max-w-2xl flex flex-col sm:flex-row gap-8 items-start">
            <img
              src="/andrea-portrait.webp"
              alt="Andrea Hurley"
              loading="lazy"
              decoding="async"
              className="w-28 h-36 sm:w-32 sm:h-40 object-cover rounded-2xl shadow-[0_18px_40px_-20px_rgba(42,33,24,0.35)]"
            />
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-euca">
                Andrea Hurley · Certified Pediatric Sleep Consultant
              </p>
              <p className="mt-4 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug">
                Andrea personally reviews
                <span className="block italic text-clay">what you share.</span>
              </p>
              <p className="mt-5 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-lg">
                Once your intake is complete, Andrea will review your
                child&rsquo;s current sleep, routines, and the challenges
                you&rsquo;re facing — so your recommendations are built around
                your actual family, not a generic schedule.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Quiet FAQ */}
        <section className="py-14 lg:py-20 border-t border-ink/10">
          <motion.div {...rise(0.1)} data-testid="welcome-faq" className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              A few things parents ask
            </p>
            <div className="mt-8 space-y-8">
              <div>
                <h3 className="font-serif font-light text-ink text-xl sm:text-2xl leading-snug">Will Andrea personally review my intake?</h3>
                <p className="mt-2 text-base font-normal leading-relaxed text-smoke">Yes — personally. Reviewing what you share is how she prepares your recommendations.</p>
              </div>
              <div>
                <h3 className="font-serif font-light text-ink text-xl sm:text-2xl leading-snug">What happens after I submit it?</h3>
                <p className="mt-2 text-base font-normal leading-relaxed text-smoke">
                  The moment you submit, you&rsquo;ll move straight to
                  scheduling your 45-minute Sleep Plan Review — and Andrea
                  begins reviewing right away.
                </p>
              </div>
              <div>
                <h3 className="font-serif font-light text-ink text-xl sm:text-2xl leading-snug">Can I come back later?</h3>
                <p className="mt-2 text-base font-normal leading-relaxed text-smoke">
                  Yes — your confirmation email has this page&rsquo;s link, and it
                  keeps working. The intake form itself is best finished in one
                  sitting.
                </p>
              </div>
              <div>
                <h3 className="font-serif font-light text-ink text-xl sm:text-2xl leading-snug">What if I have trouble?</h3>
                <p className="mt-2 text-base font-normal leading-relaxed text-smoke">
                  Text Andrea — {ANDREA_PHONE_DISPLAY}. She&rsquo;d rather you ask.
                </p>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      <div className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 pb-10">
        <Thread className="text-clay/40" />
      </div>
    </div>
  );
};
